/**
 * Migration helpers — convert legacy AttackDefendFields into the new
 * structured `counterRules` and `mechanics` shapes. Migration runs lazily
 * on read in the engine and the card editor, so legacy cards keep working
 * until they are saved through the new editor.
 *
 * Rules:
 *  - If the new field is already present and non-empty, leave it alone.
 *  - Otherwise build the new field from the legacy one.
 *  - Never delete the legacy field — the editor strips them on save.
 */
import type {
  AttackDefendFields,
  Card,
  CounterGrant,
  CounterRule,
  EffectRow,
  KeywordConfig,
  KeywordInstanceConfig,
  KeywordSlots,
  MechanicEntry,
} from "@/types/card";

// ─── Public entry points ───────────────────────────────────────────────────

export function migrateAttackDefendFields(
  fields: AttackDefendFields | undefined,
): AttackDefendFields | undefined {
  if (!fields) return fields;
  const next: AttackDefendFields = { ...fields };
  // Counter rules
  if (!next.counterRules || next.counterRules.length === 0) {
    const migrated = legacyCounterToRules(fields);
    if (migrated.length > 0) next.counterRules = migrated;
  }
  // Mechanics
  if (Array.isArray(next.mechanics) && next.mechanics.length > 0) {
    next.mechanics = next.mechanics.map((m) => migrateMechanic(m));
  }
  return next;
}

export function migrateCard(card: Card): Card {
  let next = card;
  if (next.attackDefendFields) {
    const ad = migrateAttackDefendFields(next.attackDefendFields);
    if (ad !== next.attackDefendFields) next = { ...next, attackDefendFields: ad };
  }
  next = migrateKeywordChips(next);
  next = migrateKeywordSlots(next);
  return next;
}

// ─── Keyword slot migration ───────────────────────────────────────

/** Migrate legacy shared `keywordConfig` and top-level `keywords` into
 *  per-slot `keywordSlots`. Non-destructive — legacy fields are kept so the
 *  engine fallback paths still work for cards not yet re-saved. */
function migrateKeywordSlots(card: Card): Card {
  const slots: KeywordSlots = {
    attack: { ...(card.keywordSlots?.attack ?? {}) },
    defend: { ...(card.keywordSlots?.defend ?? {}) },
  };

  // Fold top-level `keywords` into both slots (keyword present but no config
  // is legal — represented as an empty entry). This way removing the legacy
  // top-level list doesn't lose information.
  const topLevel = card.keywords ?? [];
  for (const kw of topLevel) {
    const lc = kw.toLowerCase();
    if (!lc) continue;
    if (!(lc in slots.attack!)) slots.attack![lc] = {};
    if (!(lc in slots.defend!)) slots.defend![lc] = {};
  }

  // Ensure each name in attackKeywords/defendKeywords has a slot entry.
  for (const kw of card.attackKeywords ?? []) {
    const lc = kw.toLowerCase();
    if (lc && !(lc in slots.attack!)) slots.attack![lc] = {};
  }
  for (const kw of card.defendKeywords ?? []) {
    const lc = kw.toLowerCase();
    if (lc && !(lc in slots.defend!)) slots.defend![lc] = {};
  }

  // Migrate legacy shared keywordConfig to slot config (if slot doesn't
  // already have it). Reflect/Perry → defend slot. Disarm → attack slot.
  const legacy = card.keywordConfig ?? {};
  if (typeof legacy.reflect === "number") {
    const cur = slots.defend!["reflect"] ?? {};
    if (cur.reflect === undefined) {
      slots.defend!["reflect"] = { ...cur, reflect: legacy.reflect };
    }
  }
  if (typeof legacy.disarm === "number") {
    const cur = slots.attack!["disarm"] ?? {};
    if (cur.disarm === undefined) {
      slots.attack!["disarm"] = { ...cur, disarm: legacy.disarm };
    }
  }
  if (legacy.perry) {
    const cur = slots.defend!["perry"] ?? {};
    if (cur.perry === undefined) {
      slots.defend!["perry"] = { ...cur, perry: legacy.perry };
    }
  }

  // No-op if nothing changed.
  const before = JSON.stringify(card.keywordSlots ?? {});
  const after = JSON.stringify(slots);
  if (before === after) return card;
  return { ...card, keywordSlots: slots };
}

// ─── Keyword chip migration ───────────────────────────────────────────

/** Rewrite legacy chips:
 *    "Reflect 2"  → "Reflect"      + keywordConfig.reflect = 2
 *    "Reflect N"  → "Reflect"      + keywordConfig.reflect = N
 *    "Disarm N"   → "Disarm"       + keywordConfig.disarm  = N
 *    "Perry(...)" → "Perry"        (no payload — designer must re-author)
 *  Applies to top-level keywords, attackKeywords, and defendKeywords. */
function migrateKeywordChips(card: Card): Card {
  const cfg: KeywordConfig = { ...(card.keywordConfig ?? {}) };
  let changed = false;

  const rewriteList = (list: string[] | undefined): string[] | undefined => {
    if (!list) return list;
    let mutated = false;
    const out: string[] = [];
    const seen = new Set<string>();
    for (const raw of list) {
      const k = raw.trim();
      let mapped = k;
      // Reflect N
      const reflectMatch = k.match(/^Reflect(?:\s+(\d+))?$/i);
      if (reflectMatch) {
        mapped = "Reflect";
        const num = reflectMatch[1] ? parseInt(reflectMatch[1], 10) : NaN;
        if (!Number.isNaN(num) && cfg.reflect === undefined) {
          cfg.reflect = num;
        }
      }
      // Disarm N
      const disarmMatch = k.match(/^Disarm(?:\s+(\d+))?$/i);
      if (disarmMatch) {
        mapped = "Disarm";
        const num = disarmMatch[1] ? parseInt(disarmMatch[1], 10) : NaN;
        if (!Number.isNaN(num) && cfg.disarm === undefined) {
          cfg.disarm = num;
        }
      }
      // Perry(...)
      if (/^Perry\(.*\)$/i.test(k) || /^Perry$/i.test(k)) {
        mapped = "Perry";
      }
      if (mapped !== k) mutated = true;
      // De-dupe — keyword chips are sets.
      const lower = mapped.toLowerCase();
      if (seen.has(lower)) {
        mutated = true;
        continue;
      }
      seen.add(lower);
      out.push(mapped);
    }
    if (mutated) {
      changed = true;
      return out;
    }
    return list;
  };

  const keywords = rewriteList(card.keywords);
  const attackKeywords = rewriteList(card.attackKeywords);
  const defendKeywords = rewriteList(card.defendKeywords);
  const cfgChanged =
    JSON.stringify(cfg) !== JSON.stringify(card.keywordConfig ?? {});

  if (!changed && !cfgChanged) return card;

  return {
    ...card,
    keywords: keywords ?? card.keywords,
    attackKeywords: attackKeywords ?? card.attackKeywords,
    defendKeywords: defendKeywords ?? card.defendKeywords,
    keywordConfig: cfgChanged ? cfg : card.keywordConfig,
  };
}

// ─── Counter migration ─────────────────────────────────────────────────────

function legacyCounterToRules(f: AttackDefendFields): CounterRule[] {
  const grants = f.counterGrants ?? [];
  if (grants.length === 0) return [];

  // Trigger: actionPlayed inferred from counterScope.
  const scope = f.counterScope ?? "any";
  const actionPlayed: CounterRule["trigger"]["actionPlayed"] =
    scope === "attack" ? "attack" : scope === "defend" ? "defend" : "either";

  const condType = f.counterConditionType ?? "always";
  const condVal = (f.counterConditionValue ?? "").trim();
  const weights: string[] = [];
  const attackTypes: string[] = [];
  const defendTypes: string[] = [];
  if (condType === "opponent_card_weight" && condVal) {
    weights.push(condVal);
  } else if (condType === "opponent_card_type" && condVal) {
    // Unknown which side — add to both. Eligibility check uses an OR match.
    attackTypes.push(condVal);
    defendTypes.push(condVal);
  }

  const effects: EffectRow[] = grants.map(grantToEffectRow);

  return [
    {
      trigger: {
        actionPlayed,
        weights,
        attackTypes,
        defendTypes,
        hasKeywords: [],
      },
      effects,
    },
  ];
}

function grantToEffectRow(g: CounterGrant): EffectRow {
  // All legacy counter grants buffed self on next attack/defend.
  switch (g.kind) {
    case "power-bonus":
      return {
        direction: "buff-self",
        target: "my-next-attack",
        effect: "power",
        amount: g.amount ?? 0,
      };
    case "block-bonus":
      return {
        direction: "buff-self",
        target: "my-next-defend",
        effect: "block",
        amount: g.amount ?? 0,
      };
    case "cost-reduction":
      return {
        direction: "buff-self",
        target: "my-next-action",
        effect: "stamina-cost",
        amount: g.amount ?? 0,
      };
    case "gain-stun":
      return {
        direction: "buff-self",
        target: "my-next-attack",
        effect: "add-keyword",
        keyword: "Stun",
      };
    case "gain-quick":
      return {
        direction: "buff-self",
        target: "my-next-attack",
        effect: "add-keyword",
        keyword: "Quick",
      };
    case "gain-piercing":
      return {
        direction: "buff-self",
        target: "my-next-attack",
        effect: "add-keyword",
        keyword: "Piercing",
      };
     case "gain-chain":
      return {
        direction: "buff-self",
        target: "my-next-attack",
        effect: "add-keyword",
        keyword: "Chain",
      };
    case "gain-reflect":
      return {
        direction: "buff-self",
        target: "my-next-defend",
        effect: "add-keyword",
        keyword: `Reflect ${g.power ?? g.amount ?? ""}`.trim(),
      };
    case "gain-disarm":
      return {
        direction: "buff-self",
        target: "my-next-attack",
        effect: "add-keyword",
        keyword: `Disarm ${g.count ?? g.amount ?? ""}`.trim(),
      };
    default:
      // Should not happen — fall back to a no-op-ish row the editor can clean up.
      return {
        direction: "buff-self",
        target: "my-next-action",
        effect: "power",
        amount: 0,
      };
  }
}

// ─── Mechanic migration ────────────────────────────────────────────────────

function migrateMechanic(m: MechanicEntry): MechanicEntry {
  // Already structured? Keep as-is.
  if (
    Array.isArray(m.effects) &&
    m.effects.length > 0 &&
    typeof m.appliesWhen === "string" &&
    typeof m.condition === "string"
  ) {
    return m;
  }

  // Legacy → structured
  const trigger = (m.trigger ?? "attack") as "attack" | "defend";
  const appliesWhen = trigger;
  const condition = mapLegacyCondition(m.conditionType);
  const effects = mapLegacyEffect(
    m.effectType ?? "",
    Number(m.effectAmount ?? 0),
    (m.effectScope ?? "any") as "any" | "attack" | "defend",
  );
  return { appliesWhen, condition, effects };
}

function mapLegacyCondition(c: string | undefined): MechanicEntry["condition"] {
  switch ((c ?? "").trim()) {
    case "on_attack_damage":
      return "attack-deals-damage";
    case "on_defend_full_block":
      return "block-fully-blocks";
    default:
      // hp-based / weapons-exhausted conditions don't map to the new model.
      // Fall back to "none" — designer can re-author if needed.
      return "none";
  }
}

function mapLegacyEffect(
  effectType: string,
  amount: number,
  scope: "any" | "attack" | "defend",
): EffectRow[] {
  const targetForScope = (): EffectRow["target"] =>
    scope === "attack"
      ? "my-next-attack"
      : scope === "defend"
        ? "my-next-defend"
        : "my-next-action";
  const oppTargetForScope = (): EffectRow["target"] =>
    scope === "attack"
      ? "my-next-attack"
      : scope === "defend"
        ? "my-next-defend"
        : "my-next-action";

  switch (effectType) {
    case "next-cost-reduction":
      return [
        {
          direction: "buff-self",
          target: targetForScope(),
          effect: "stamina-cost",
          amount,
        },
      ];
    case "next-power-bonus":
      return [
        { direction: "buff-self", target: targetForScope(), effect: "power", amount },
      ];
    case "next-block-bonus":
      return [
        { direction: "buff-self", target: targetForScope(), effect: "block", amount },
      ];
    case "next-gain-stun":
      return [
        {
          direction: "buff-self",
          target: targetForScope(),
          effect: "add-keyword",
          keyword: "Stun",
        },
      ];
    case "next-gain-quick":
      return [
        {
          direction: "buff-self",
          target: targetForScope(),
          effect: "add-keyword",
          keyword: "Quick",
        },
      ];
    case "next-gain-piercing":
      return [
        {
          direction: "buff-self",
          target: targetForScope(),
          effect: "add-keyword",
          keyword: "Piercing",
        },
      ];
    case "next-gain-reflect":
      return [
        {
          direction: "buff-self",
          target: targetForScope(),
          effect: "add-keyword",
          keyword: `Reflect ${amount || ""}`.trim(),
        },
      ];
    case "next-gain-disarm":
      return [
        {
          direction: "buff-self",
          target: targetForScope(),
          effect: "add-keyword",
          keyword: `Disarm ${amount || ""}`.trim(),
        },
      ];
    case "self-power-bonus":
      return [
        { direction: "buff-self", target: "this-attack", effect: "power", amount },
      ];
    case "self-block-bonus":
      return [
        { direction: "buff-self", target: "this-defend", effect: "block", amount },
      ];
    case "self-gain-piercing":
      return [
        {
          direction: "buff-self",
          target: "this-attack",
          effect: "add-keyword",
          keyword: "Piercing",
        },
      ];
    case "self-gain-stun":
      return [
        {
          direction: "buff-self",
          target: "this-attack",
          effect: "add-keyword",
          keyword: "Stun",
        },
      ];
    case "self-gain-quick":
      return [
        {
          direction: "buff-self",
          target: "this-attack",
          effect: "add-keyword",
          keyword: "Quick",
        },
      ];
    case "self-gain-disarm":
      return [
        {
          direction: "buff-self",
          target: "this-attack",
          effect: "add-keyword",
          keyword: `Disarm ${amount || ""}`.trim(),
        },
      ];
    case "self-gain-reflect":
      return [
        {
          direction: "buff-self",
          target: "this-defend",
          effect: "add-keyword",
          keyword: `Reflect ${amount || ""}`.trim(),
        },
      ];
    case "opp-reduce-power":
      return [
        {
          direction: "debuff-opponent",
          target: oppTargetForScope() === "my-next-action" ? "my-next-action" : "my-next-attack",
          effect: "power",
          amount,
        },
      ];
    case "opp-reduce-block":
      return [
        {
          direction: "debuff-opponent",
          target: "my-next-defend",
          effect: "block",
          amount,
        },
      ];
    case "opp-remove-power-buffs":
      return [
        {
          direction: "debuff-opponent",
          target: "my-next-attack",
          effect: "remove-power-buffs",
        },
      ];
    case "opp-remove-block-buffs":
      return [
        {
          direction: "debuff-opponent",
          target: "my-next-defend",
          effect: "remove-block-buffs",
        },
      ];
    case "opp-remove-keywords":
      return [
        {
          direction: "debuff-opponent",
          target: "my-next-action",
          effect: "remove-keyword",
          keyword: "",
        },
      ];
    // gain-stamina / gain-life / add-armor-wear don't map — they are
    // self-state mutations that aren't part of the new effect-row model.
    // Leave unmapped (returns empty list); the in-engine evaluator treats
    // empty `effects` as a no-op so legacy cards relying on these keep
    // working via the existing applyResults path until re-authored.
    default:
      return [];
  }
}
